import type { ReactNode } from 'react';

/** Remove leading @ and trim. */
export function stripSocialHandle(s: string): string {
  return s.replace(/^@+/, '').trim();
}

/**
 * Renders a handle that opens the platform profile in a new tab when we can build a safe URL;
 * otherwise shows plain text (e.g. "—" or WeChat with no public URL).
 */
export function SocialHandleLink({
  href,
  children,
  className = '',
}: {
  href: string | null;
  children: ReactNode;
  className?: string;
}) {
  if (!href) {
    return <span className={className}>{children}</span>;
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
}

const PLACEHOLDER = '—';

function isMissingDisplay(handle: string | undefined | null): boolean {
  if (handle == null) return true;
  const t = String(handle).trim();
  return t === '' || t === PLACEHOLDER;
}

/**
 * @returns Public Instagram profile URL, or an existing instagr.am / instagram.com link as-is.
 */
export function instagramProfileUrl(handleOrUrl: string | undefined | null): string | null {
  if (isMissingDisplay(handleOrUrl)) return null;
  const t = String(handleOrUrl).trim();
  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      if (u.hostname.includes('instagram.com') || u.hostname.includes('instagr.am')) return u.toString();
    } catch {
      return null;
    }
    return null;
  }
  const h = stripSocialHandle(t);
  if (!h) return null;
  return `https://www.instagram.com/${encodeURIComponent(h)}/`;
}

/**
 * @returns TikTok profile URL.
 */
export function tiktokProfileUrl(handle: string | undefined | null): string | null {
  if (isMissingDisplay(handle)) return null;
  const t = String(handle).trim();
  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      if (u.hostname.includes('tiktok.com')) return u.toString();
    } catch {
      return null;
    }
    return null;
  }
  const h = stripSocialHandle(t);
  if (!h) return null;
  return `https://www.tiktok.com/@${encodeURIComponent(h)}`;
}

/**
 * YouTube channel: accepts full video/channel URLs, @handles, or legacy /c/ and /user/ paths.
 */
export function youtubeProfileUrl(channel: string | undefined | null): string | null {
  if (isMissingDisplay(channel)) return null;
  const t = String(channel).trim();
  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      if (u.hostname.includes('youtube.com') || u.hostname === 'youtu.be') return u.toString();
    } catch {
      return null;
    }
    return null;
  }
  if (/^\/\//.test(t)) {
    return `https:${t}`;
  }
  if (!/^https?:\/\//i.test(t) && /youtube\.com\//i.test(t)) {
    return `https://${t.replace(/^\/\//, '')}`;
  }
  if (!/^https?:\/\//i.test(t) && /youtu\.be\//i.test(t)) {
    return `https://${t.replace(/^\/\//, '')}`;
  }
  // Channel ID (e.g. UC…)
  if (/^UC[-_a-zA-Z0-9]{10,}$/.test(t)) {
    return `https://www.youtube.com/channel/${encodeURIComponent(t)}`;
  }
  const h = stripSocialHandle(t);
  if (!h) return null;
  return `https://www.youtube.com/@${encodeURIComponent(h)}`;
}

/**
 * Facebook profile or page. Numeric-only strings are treated as profile id (profile.php).
 */
export function facebookProfileUrl(handle: string | undefined | null): string | null {
  if (isMissingDisplay(handle)) return null;
  const t = String(handle).trim();
  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      if (u.hostname.includes('facebook.com') || u.hostname.includes('fb.com') || u.hostname.includes('fb.me'))
        return u.toString();
    } catch {
      return null;
    }
    return null;
  }
  if (/^facebook\.com\//i.test(t) || /^m\.facebook\.com\//i.test(t)) {
    return `https://${t.replace(/^\/\//, '')}`;
  }
  if (/^\d{5,24}$/.test(t)) {
    return `https://www.facebook.com/profile.php?id=${encodeURIComponent(t)}`;
  }
  const h = stripSocialHandle(t);
  if (!h) return null;
  return `https://www.facebook.com/${encodeURIComponent(h)}`;
}
